"use client";

import { apiOauthCallback } from "@/api/auth";
import { OAuthLoadingModal } from "@/components/auth/OAuthLoadingModal";
import { SocialLoginGoogle } from "@/components/auth/SocialLogin-google";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { toast } from "@/components/ui/showToast";
import { SmartImage } from "@/components/ui/smart-image";
import { useLoginModal } from "@/contexts/login-modal-context";
import { useThemeMode } from "@/features/theme/theme-provider";
import { useAuthRedirect } from "@/hooks/use-auth-redirect";
import { getBrandLogo, getBrandLogoDark, getBrandName } from "@/lib/brand-config";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth";
import { useUserInfo } from "@/store/user-info";
import { refreshNativeCache } from "@/utils/interface";
import { useAppRouter } from "@/utils/router";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const GOOGLE_LOGIN_SOURCE = "google";
const GOOGLE_OAUTH_POPUP_MESSAGE_TYPE = "predik:google-oauth-callback";
const AUTH_STORAGE_KEY = "auth";
const DEFAULT_GOOGLE_LOGIN_ERROR = "Google login failed";
const OAUTH_SEARCH_KEYS = [ "result", "source", "state", "isNewUser", "msg", "message", "error" ] as const;

interface OAuthCallbackParams {
    result?: string;
    source?: string;
    state?: string;
    token?: string;
    error?: string;
}

interface OAuthCallbackResult {
    ok: boolean;
    token?: string;
    error?: string;
}

interface OAuthState {
    isProcessing: boolean;
    error?: string;
}

function isOAuthPopupWindow() {
    if ( typeof window === "undefined" ) return false;

    return !!window.opener && window.opener !== window;
}

function getOAuthCallbackParamsFromSearch( searchParams: URLSearchParams ): OAuthCallbackParams {
    return {
        result: searchParams.get( "result" ) || undefined,
        source: searchParams.get( "source" ) || undefined,
        state: searchParams.get( "state" ) || undefined,
        error: searchParams.get( "msg" ) || searchParams.get( "message" ) || searchParams.get( "error" ) || undefined,
    };
}

function getStringField( value: unknown ) {
    return typeof value === "string" ? value : undefined;
}

function getOAuthCallbackParamsFromMessage( data: unknown ): OAuthCallbackParams | undefined {
    if ( !data || typeof data !== "object" ) return;

    const message = data as Record<string, unknown>;
    if ( message.type !== GOOGLE_OAUTH_POPUP_MESSAGE_TYPE ) return;

    return {
        result: getStringField( message.result ),
        source: getStringField( message.source ),
        state: getStringField( message.state ),
        token: getStringField( message.token ),
        error: getStringField( message.error ),
    };
}

function postOAuthCallbackToOpener( callbackParams: OAuthCallbackParams ) {
    if ( !isOAuthPopupWindow() ) return;

    try {
        window.opener.postMessage( {
            type: GOOGLE_OAUTH_POPUP_MESSAGE_TYPE,
            result: callbackParams.result,
            source: callbackParams.source,
            state: callbackParams.state,
            token: callbackParams.token,
            error: callbackParams.error,
        }, window.location.origin );
    } catch ( error ) {
        console.error( "Google OAuth opener notification failed", error );
    }
}

function getApiErrorMessage( value: unknown, fallback = DEFAULT_GOOGLE_LOGIN_ERROR ) {
    if ( typeof value === "string" ) {
        const message = value.trim();
        return message || fallback;
    }

    if ( value instanceof Error ) {
        const message = value.message.trim();
        return message || fallback;
    }

    if ( !value || typeof value !== "object" ) return fallback;

    const record = value as Record<string, unknown>;
    const msg = getStringField( record.msg )?.trim();
    if ( msg ) return msg;

    const message = getStringField( record.message )?.trim();
    if ( message ) return message;

    return fallback;
}

function getTokenFromAuthStorageValue( value: string ) {
    try {
        const parsed = JSON.parse( value ) as unknown;
        if ( !parsed || typeof parsed !== "object" ) return;

        const state = ( parsed as Record<string, unknown> ).state;
        if ( !state || typeof state !== "object" ) return;

        const token = ( state as Record<string, unknown> ).token;
        return typeof token === "string" && token ? token : undefined;
    } catch {
        return;
    }
}

function getTokenFromCurrentAuthStorage() {
    if ( typeof window === "undefined" ) return;

    const value = window.localStorage.getItem( AUTH_STORAGE_KEY );
    if ( !value ) return;

    return getTokenFromAuthStorageValue( value );
}

export function GoogleLoginModal() {
    const router = useAppRouter();
    const searchParams = useSearchParams();
    const { open, closeModal, openModal } = useLoginModal();
    const { themeMode } = useThemeMode();
    const { hydrated: isAuthHydrated } = useAuthRedirect();
    const token = useAuthStore( state => state.token );
    const setToken = useAuthStore( state => state.setToken );
    const { refreshUserInfo } = useUserInfo();

    const [ oauthState, setOAuthState ] = useState<OAuthState>( { isProcessing: false } );
    const processedOAuthKeyRef = useRef( "" );

    const brandName = useMemo( () => getBrandName(), [] );
    const brandLogo = useMemo( () => {
        return themeMode === "dark" ? getBrandLogoDark() : getBrandLogo();
    }, [ themeMode ] );

    const clearOAuthSearchParams = useCallback( () => {
        if ( typeof window === "undefined" ) return;

        const params = new URLSearchParams( window.location.search );
        let shouldReplace = false;

        OAUTH_SEARCH_KEYS.forEach( key => {
            if ( !params.has( key ) ) return;
            params.delete( key );
            shouldReplace = true;
        } );

        if ( !shouldReplace ) return;

        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
        router.replace( nextUrl );
    }, [ router ] );

    const handleOAuthFailure = useCallback( ( message: string, shouldOpenModal = !token ) => {
        if ( !shouldOpenModal ) {
            setOAuthState( { isProcessing: false } );
            closeModal();
            clearOAuthSearchParams();
            return;
        }

        setOAuthState( { isProcessing: false, error: message } );
        openModal( "manual" );
        toast.error( message );
        clearOAuthSearchParams();
    }, [ clearOAuthSearchParams, closeModal, openModal, token ] );

    const handleOAuthCallback = useCallback(
        async (
            callbackParams?: OAuthCallbackParams,
            options: { shouldHandleFailure?: boolean } = {},
        ): Promise<OAuthCallbackResult> => {
            const { shouldHandleFailure = true } = options;
            const fallbackParams = getOAuthCallbackParamsFromSearch( searchParams );
            const result = callbackParams?.result ?? fallbackParams.result;
            const source = callbackParams?.source ?? fallbackParams.source;
            const state = callbackParams?.state ?? fallbackParams.state;

            const failOAuth = ( message: string ): OAuthCallbackResult => {
                if ( shouldHandleFailure ) {
                    handleOAuthFailure( message, true );
                } else {
                    setOAuthState( { isProcessing: false } );
                    clearOAuthSearchParams();
                }

                return { ok: false, error: message };
            };

            if ( !result && !source && !state ) return { ok: false, error: DEFAULT_GOOGLE_LOGIN_ERROR };

            if ( result !== "success" || source !== GOOGLE_LOGIN_SOURCE || !state ) {
                return failOAuth( getApiErrorMessage( callbackParams?.error ) );
            }

            try {
                setOAuthState( { isProcessing: true } );

                const [ resOk, resData ] = await apiOauthCallback( { state, source } );

                if ( !resOk ) {
                    return failOAuth( getApiErrorMessage( resData ) );
                }

                const tokenData = resData?.data?.token || resData?.data;
                const nextToken = typeof tokenData === "string" ? tokenData : "";

                if ( !nextToken ) {
                    return failOAuth( getApiErrorMessage( resData ) );
                }

                refreshNativeCache();
                setToken( nextToken );
                setOAuthState( { isProcessing: false } );
                closeModal();
                clearOAuthSearchParams();

                try {
                    await refreshUserInfo( "login-modal:google" );
                } catch ( error ) {
                    console.error( "Post-login user info refresh failed", error );
                }
                return { ok: true, token: nextToken };
            } catch ( error ) {
                console.error( "Google login callback failed", error );
                return failOAuth( getApiErrorMessage( error ) );
            }
        },
        [ clearOAuthSearchParams, closeModal, handleOAuthFailure, refreshUserInfo, searchParams, setToken ],
    );

    useEffect( () => {
        const completeAuthSync = ( source: string ) => {
            refreshNativeCache();
            setOAuthState( { isProcessing: false } );
            closeModal();
            clearOAuthSearchParams();
            void refreshUserInfo( source );
            return true;
        };

        const syncAuthToken = ( nextToken: string, source: string ) => {
            if ( nextToken === token ) return completeAuthSync( source );

            setToken( nextToken );
            return completeAuthSync( source );
        };

        const syncAuthTokenFromStorage = ( source: string ) => {
            if ( !open ) return false;

            const nextToken = getTokenFromCurrentAuthStorage();
            if ( !nextToken ) return false;

            return syncAuthToken( nextToken, source );
        };

        const handleFocus = () => {
            syncAuthTokenFromStorage( "login-modal:google-focus" );
        };

        const handleVisibilityChange = () => {
            if ( document.visibilityState !== "visible" ) return;

            syncAuthTokenFromStorage( "login-modal:google-visible" );
        };

        const handleMessage = ( event: MessageEvent<unknown> ) => {
            if ( event.origin !== window.location.origin ) return;

            const callbackParams = getOAuthCallbackParamsFromMessage( event.data );
            if ( !callbackParams ) return;

            const oauthKey = `${callbackParams.result || ""}:${callbackParams.source || ""}:${
                callbackParams.state || ""
            }`;
            if ( processedOAuthKeyRef.current === oauthKey ) return;

            processedOAuthKeyRef.current = oauthKey;

            if (
                callbackParams.result !== "success"
                || callbackParams.source !== GOOGLE_LOGIN_SOURCE
                || !callbackParams.state
            ) {
                handleOAuthFailure( getApiErrorMessage( callbackParams.error ), true );
                return;
            }

            if ( callbackParams.token ) {
                syncAuthToken( callbackParams.token, "login-modal:google-message" );
                return;
            }

            if ( syncAuthTokenFromStorage( "login-modal:google-message" ) ) return;

            handleOAuthFailure( getApiErrorMessage( callbackParams.error ), true );
        };

        window.addEventListener( "focus", handleFocus );
        window.addEventListener( "message", handleMessage );
        document.addEventListener( "visibilitychange", handleVisibilityChange );

        return () => {
            window.removeEventListener( "focus", handleFocus );
            window.removeEventListener( "message", handleMessage );
            document.removeEventListener( "visibilitychange", handleVisibilityChange );
        };
    }, [ clearOAuthSearchParams, closeModal, handleOAuthFailure, open, refreshUserInfo, setToken, token ] );

    useEffect( () => {
        const result = searchParams.get( "result" );
        const source = searchParams.get( "source" );
        const state = searchParams.get( "state" );

        if ( !result && !source && !state ) return;
        if ( !isAuthHydrated ) return;

        const oauthKey = `${result || ""}:${source || ""}:${state || ""}`;
        if ( processedOAuthKeyRef.current === oauthKey ) return;

        processedOAuthKeyRef.current = oauthKey;

        if ( isOAuthPopupWindow() ) {
            const callbackParams = {
                result: result || undefined,
                source: source || undefined,
                state: state || undefined,
            };

            void ( async () => {
                const callbackResult = await handleOAuthCallback( callbackParams, { shouldHandleFailure: false } );
                postOAuthCallbackToOpener( {
                    ...callbackParams,
                    result: callbackResult.ok ? callbackParams.result : "failed",
                    token: callbackResult.token,
                    error: callbackResult.error,
                } );
                window.close();
            } )();
            return;
        }

        const isGoogleOAuthSuccess = result === "success" && source === GOOGLE_LOGIN_SOURCE && !!state;

        if ( token && !isGoogleOAuthSuccess ) {
            setOAuthState( { isProcessing: false } );
            closeModal();
            clearOAuthSearchParams();
            return;
        }

        void handleOAuthCallback();
    }, [ clearOAuthSearchParams, closeModal, handleOAuthCallback, isAuthHydrated, searchParams, token ] );

    const handleOpenChange = ( nextOpen: boolean ) => {
        if ( nextOpen || oauthState.isProcessing ) return;
        setOAuthState( { isProcessing: false } );
        closeModal();
    };

    const callbackSource = searchParams.get( "source" );
    const hasOAuthCallbackParams = callbackSource === GOOGLE_LOGIN_SOURCE
        && !!( searchParams.get( "result" ) || searchParams.get( "state" ) );
    const isOAuthLoadingOpen = oauthState.isProcessing || hasOAuthCallbackParams;

    return (
        <>
            <OAuthLoadingModal open={isOAuthLoadingOpen} source={GOOGLE_LOGIN_SOURCE} />

            <Sheet open={open} onOpenChange={handleOpenChange}>
                <SheetContent
                    side="bottom"
                    showCloseButton={false}
                    className="mx-auto w-full max-w-[500px] gap-0 rounded-t-[24px] rounded-b-none border-0 bg-surface-card p-0 text-content-primary shadow-none"
                >
                    <div className="w-full px-8 pb-8 pt-8 shadow-[0_-12px_48px_var(--ui-overlay-subtle)]">
                        <div className="flex items-center gap-[14px]">
                            <SmartImage
                                src={brandLogo}
                                alt={`${brandName} logo`}
                                height={31}
                                width={90}
                                className="h-[31px] w-[90px] flex-shrink-0 object-contain"
                            />
                        </div>

                        <div className="mt-[52px] flex flex-col items-center gap-3 text-center">
                            <SheetTitle className="text-center text-[28px] font-bold leading-normal text-content-primary">
                                Welcome to {brandName}
                            </SheetTitle>
                            <SheetDescription className="text-center text-base font-normal leading-6 text-content-secondary">
                                Sign in with Google to continue
                            </SheetDescription>
                        </div>

                        {oauthState.error
                            ? (
                                <p className="mt-5 text-center text-sm font-medium text-state-danger">
                                    {oauthState.error}
                                </p>
                            )
                            : undefined}

                        <div className={cn( "w-full", oauthState.error ? "mt-5" : "mt-[52px]" )}>
                            <SocialLoginGoogle />
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
